import { Component } from '@theme/component';

/**
 * The filter set is rendered twice on a collection page: once as the inline
 * sidebar and once inside the filters drawer. The two are the same list — 1,149
 * nodes and 150 inputs each — and only one of them is ever shown. Below 750px
 * `.facets--vertical` is `display: none` and the drawer is what opens; at 750px
 * and up the sidebar is the filter UI and the drawer's dialog is `display: none`.
 * So roughly 200KB of markup and a quarter of the page's DOM nodes are built on
 * every collection load for a list the visitor cannot reach.
 *
 * Each copy now ships inside a `<template>`, which the parser turns into an inert
 * fragment: no elements in the document, no style or layout work, nothing for
 * morph to walk. The copy that the current breakpoint actually shows is cloned
 * back out; the other one stays inert until a resize crosses the breakpoint.
 *
 * Hydration has to happen in two places. On first load an inline script next to
 * the template does it while the parser is still working, so the sidebar is
 * present before the first paint — this module is a deferred module and would
 * arrive too late to avoid a flash. After that, `updatedCallback` takes over: the
 * Section Rendering API hands morph fresh markup on every filter change, and that
 * markup always contains the template again, so the active copy needs cloning
 * back each time. morph only calls `updatedCallback` on real `Component`
 * instances (see `onAfterUpdate` in morph.js), which is why this extends it.
 */

const DESKTOP_QUERY = '(min-width: 750px)';
const desktop = window.matchMedia(DESKTOP_QUERY);

/**
 * Clone a host's template into the document, if this breakpoint is the one that
 * shows it. Safe to call repeatedly: once the template is gone there is nothing
 * left to match, so a second call is a no-op.
 *
 * @param {Element} host - A `<deferred-facets>` element.
 */
export function hydrateDeferredFacets(host) {
  if (!(host instanceof HTMLElement)) return;

  const wantsDesktop = host.getAttribute('data-active-when') === 'desktop';
  if (wantsDesktop !== desktop.matches) return;

  const template = host.querySelector(':scope > template');
  if (!(template instanceof HTMLTemplateElement)) return;

  template.replaceWith(template.content.cloneNode(true));
}

/**
 * @extends {Component}
 */
class DeferredFacets extends Component {
  /**
   * morph's `updatedCallback` is not a reliable trigger for the drawer copy.
   * `<theme-drawer>` carries `data-skip-node-update`, and morph answers that by
   * recursing into children *without* calling `updateNode` on the drawer — which
   * is where `onAfterUpdate`, and so `updatedCallback`, is dispatched from. The
   * sidebar copy has no such ancestor and does get the callback, so relying on it
   * alone left the drawer empty after any filter applied from inside it.
   *
   * Watching our own `childList` catches every path instead, because whatever
   * mechanism puts the `<template>` back has to touch our direct children to do
   * it. Re-entry is harmless: hydrating removes the template, so the mutation
   * that hydration itself triggers finds nothing left to do.
   *
   * @type {MutationObserver}
   */
  #observer = new MutationObserver(() => hydrateDeferredFacets(this));

  connectedCallback() {
    super.connectedCallback();
    hydrateDeferredFacets(this);
    this.#observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    hydrateDeferredFacets(this);
  }
}

/**
 * Crossing the breakpoint promotes the copy that just became visible. The one
 * that just became hidden is deliberately left alone rather than returned to a
 * template: it is `display: none` either way, and tearing down a live subtree
 * risks discarding form state mid-session for no gain a visitor can see.
 */
desktop.addEventListener('change', () => {
  for (const host of document.querySelectorAll('deferred-facets')) {
    hydrateDeferredFacets(host);
  }
});

if (!customElements.get('deferred-facets')) {
  customElements.define('deferred-facets', DeferredFacets);
}
